import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import Input from '../../components/ui/Input'
import ProviderSettingsLayout from '../../components/provider/ProviderSettingsLayout'
import {
  MY_PROVIDER_MANAGEMENT_QUERY,
  UPDATE_PROVIDER_PROFILE_MUTATION,
  UPDATE_USER_AVATAR_MUTATION,
  type MyProviderManagementData,
  type UpdateProviderProfileData,
  type UpdateProviderProfileVariables,
  type UpdateUserAvatarData,
  type UpdateUserAvatarVariables,
} from '../../graphql/providerManagement'
import { useAuth } from '../../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../../contexts/LangContext'

const LANGUAGE_OPTIONS = [
  { code: 'el', el: 'Ελληνικά', en: 'Greek' },
  { code: 'en', el: 'Αγγλικά', en: 'English' },
  { code: 'sq', el: 'Αλβανικά', en: 'Albanian' },
  { code: 'bg', el: 'Βουλγαρικά', en: 'Bulgarian' },
  { code: 'ro', el: 'Ρουμανικά', en: 'Romanian' },
  { code: 'uk', el: 'Ουκρανικά', en: 'Ukrainian' },
] as const

interface ProfileFormState {
  bio: string
  yearsExperience: string
  hourlyRate: string
  avatarUrl: string
  languages: string[]
  acceptingJobs: boolean
  instantBooking: boolean
  maxJobsPerDay: string
  bufferMinutes: string
}

const translations = {
  el: {
    title: 'Προφίλ επαγγελματία',
    body: 'Ενημέρωσε τα βασικά στοιχεία που βλέπουν οι πελάτες όταν σε ανακαλύπτουν.',
    loading: 'Φορτώνουμε τα στοιχεία σου...',
    save: 'Αποθήκευση προφίλ',
    saving: 'Αποθήκευση...',
    saved: 'Οι αλλαγές αποθηκεύτηκαν.',
    bio: 'Βιογραφικό',
    bioPlaceholder: 'Πες λίγα λόγια για την εμπειρία, την προσέγγισή σου και τα δυνατά σου σημεία.',
    experience: 'Χρόνια εμπειρίας',
    hourlyRate: 'Ωριαία χρέωση (EUR)',
    avatarUrl: 'Avatar URL',
    avatarHint: 'Το backend αποθηκεύει προσωρινά URL avatar. Το upload θα προστεθεί αργότερα.',
    languages: 'Γλώσσες',
    workPrefs: 'Ρυθμίσεις εργασίας',
    acceptingJobs: 'Δέχομαι νέες κρατήσεις',
    instantBooking: 'Άμεση online κράτηση',
    maxJobsPerDay: 'Μέγιστες δουλειές ανά μέρα',
    bufferMinutes: 'Χρόνος buffer (λεπτά)',
  },
  en: {
    title: 'Provider profile',
    body: 'Update the core details customers see when they discover your profile.',
    loading: 'Loading your profile...',
    save: 'Save profile',
    saving: 'Saving...',
    saved: 'Your changes were saved.',
    bio: 'Bio',
    bioPlaceholder: 'Describe your experience, your approach, and what customers should know about you.',
    experience: 'Years of experience',
    hourlyRate: 'Hourly rate (EUR)',
    avatarUrl: 'Avatar URL',
    avatarHint: 'The backend currently stores avatar URLs. File upload can be added later.',
    languages: 'Languages',
    workPrefs: 'Work settings',
    acceptingJobs: 'Accepting new bookings',
    instantBooking: 'Instant online booking',
    maxJobsPerDay: 'Max jobs per day',
    bufferMinutes: 'Buffer time (minutes)',
  },
} as const

export default function ProfileEditPage() {
  const { currentUser, refetchUser } = useAuth()
  const { locale, t } = useLang()
  const copy = useScopedTranslations('provider.profileEdit', translations[locale])

  const [form, setForm] = useState<ProfileFormState>({
    bio: '',
    yearsExperience: '',
    hourlyRate: '',
    avatarUrl: currentUser?.avatarUrl ?? '',
    languages: [],
    acceptingJobs: true,
    instantBooking: false,
    maxJobsPerDay: '3',
    bufferMinutes: '30',
  })
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)

  const { data, loading } = useQuery<MyProviderManagementData>(MY_PROVIDER_MANAGEMENT_QUERY)
  const [updateProviderProfile, { loading: savingProfile }] = useMutation<UpdateProviderProfileData, UpdateProviderProfileVariables>(UPDATE_PROVIDER_PROFILE_MUTATION)
  const [updateUserAvatar] = useMutation<UpdateUserAvatarData, UpdateUserAvatarVariables>(UPDATE_USER_AVATAR_MUTATION)

  useEffect(() => {
    const profile = data?.myProviderProfile
    if (!profile) return

    setForm({
      bio: profile.bio ?? '',
      yearsExperience: profile.yearsExperience?.toString() ?? '',
      hourlyRate: profile.hourlyRateCents ? String(profile.hourlyRateCents / 100) : '',
      avatarUrl: currentUser?.avatarUrl ?? '',
      languages: profile.languages,
      acceptingJobs: profile.acceptingJobs,
      instantBooking: profile.instantBooking,
      maxJobsPerDay: String(profile.maxJobsPerDay),
      bufferMinutes: String(profile.bufferMinutes),
    })
  }, [currentUser?.avatarUrl, data])

  function toggleLanguage(code: string) {
    setSaved(false)
    setForm((current) => ({
      ...current,
      languages: current.languages.includes(code)
        ? current.languages.filter((language) => language !== code)
        : [...current.languages, code],
    }))
  }

  async function handleSave() {
    setErrors([])
    setSaved(false)

    try {
      const profileResult = await updateProviderProfile({
        variables: {
          bio: form.bio.trim() || undefined,
          yearsExperience: form.yearsExperience ? Number(form.yearsExperience) : undefined,
          hourlyRateCents: form.hourlyRate ? Math.round(Number(form.hourlyRate) * 100) : undefined,
          languages: form.languages,
          acceptingJobs: form.acceptingJobs,
          instantBooking: form.instantBooking,
          maxJobsPerDay: form.maxJobsPerDay ? Number(form.maxJobsPerDay) : undefined,
          bufferMinutes: form.bufferMinutes ? Number(form.bufferMinutes) : undefined,
        },
      })

      const profileErrors = profileResult.data?.updateProviderProfile.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save the profile.')]
      if (profileErrors.length > 0) {
        setErrors(profileErrors)
        return
      }

      if (form.avatarUrl !== (currentUser?.avatarUrl ?? '')) {
        const avatarResult = await updateUserAvatar({
          variables: { avatarUrl: form.avatarUrl.trim() || undefined },
        })
        const avatarErrors = avatarResult.data?.updateProfile.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση avatar.', 'Could not save the avatar.')]

        if (avatarErrors.length > 0) {
          setErrors(avatarErrors)
          return
        }

        await refetchUser()
      }

      setSaved(true)
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save the profile.')])
    }
  }

  return (
    <ProviderSettingsLayout title={copy.title} body={copy.body}>
      <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
        {loading ? (
          <p className="text-sm text-slate-500">{copy.loading}</p>
        ) : (
          <>
            <FormError errors={errors} />
            {saved && (
              <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {copy.saved}
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="provider-bio" className="mb-2 block text-sm font-semibold text-slate-700">
                  {copy.bio}
                </label>
                <textarea
                  id="provider-bio"
                  value={form.bio}
                  onChange={(event) => {
                    setSaved(false)
                    setForm((current) => ({ ...current, bio: event.target.value }))
                  }}
                  rows={5}
                  placeholder={copy.bioPlaceholder}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                />
              </div>

              <Input
                label={copy.experience}
                type="number"
                min="0"
                value={form.yearsExperience}
                onChange={(event) => {
                  setSaved(false)
                  setForm((current) => ({ ...current, yearsExperience: event.target.value }))
                }}
              />
              <Input
                label={copy.hourlyRate}
                type="number"
                min="0"
                step="0.5"
                value={form.hourlyRate}
                onChange={(event) => {
                  setSaved(false)
                  setForm((current) => ({ ...current, hourlyRate: event.target.value }))
                }}
              />

              <div className="md:col-span-2">
                <Input
                  label={copy.avatarUrl}
                  value={form.avatarUrl}
                  onChange={(event) => {
                    setSaved(false)
                    setForm((current) => ({ ...current, avatarUrl: event.target.value }))
                  }}
                  hint={copy.avatarHint}
                />
              </div>

              <div className="md:col-span-2">
                <p className="mb-2 text-sm font-semibold text-slate-700">{copy.languages}</p>
                <div className="flex flex-wrap gap-3">
                  {LANGUAGE_OPTIONS.map((language) => {
                    const active = form.languages.includes(language.code)
                    return (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => toggleLanguage(language.code)}
                        className={[
                          'rounded-full border px-4 py-2 text-sm font-semibold transition',
                          active
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                        ].join(' ')}
                      >
                        {language[locale]}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
              <h2 className="text-lg font-bold text-slate-950">{copy.workPrefs}</h2>
              <div className="mt-4 grid gap-5 md:grid-cols-2">
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.acceptingJobs}
                    onChange={(event) => {
                      setSaved(false)
                      setForm((current) => ({ ...current, acceptingJobs: event.target.checked }))
                    }}
                  />
                  {copy.acceptingJobs}
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.instantBooking}
                    onChange={(event) => {
                      setSaved(false)
                      setForm((current) => ({ ...current, instantBooking: event.target.checked }))
                    }}
                  />
                  {copy.instantBooking}
                </label>

                <Input
                  label={copy.maxJobsPerDay}
                  type="number"
                  min="1"
                  value={form.maxJobsPerDay}
                  onChange={(event) => {
                    setSaved(false)
                    setForm((current) => ({ ...current, maxJobsPerDay: event.target.value }))
                  }}
                />
                <Input
                  label={copy.bufferMinutes}
                  type="number"
                  min="0"
                  step="15"
                  value={form.bufferMinutes}
                  onChange={(event) => {
                    setSaved(false)
                    setForm((current) => ({ ...current, bufferMinutes: event.target.value }))
                  }}
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button type="button" size="lg" loading={savingProfile} onClick={handleSave}>
                {copy.save}
              </Button>
            </div>
          </>
        )}
      </section>
    </ProviderSettingsLayout>
  )
}
